
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import {
  ITransactionsProvider,
  ITransactionsProviderMethods
} from "@/models/providers/transactions";
import { CreateElement } from "vue";
import { IInvoice } from "@/models/invoices";
import { TransactionFilters } from "@/data/filters/transactions";
import { TransactionSorters } from "@/data/sorters/transactions";
import { TransactionStatus, TransactionTypes } from "@/data/constants";
import { IFilter, ISorter } from "@/models/tables";
import { IClient } from "@/models/clients";
import { IPaymentLog } from "@/models/paymentLog";
import { mapGetters } from "vuex";

@Component({
  computed: {
    ...mapGetters({
      isMultibrand: "brand/isMultibrand"
    })
  }
})
export default class TransactionsProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({
    default: () => [
      TransactionTypes.SALE_DIRECT,
      TransactionTypes.AUTHORIZE_DIRECT,
      TransactionTypes.SALE_LINK,
      TransactionTypes.AUTHORIZE_LINK,
      TransactionTypes.SALE_EXECUTE,
      TransactionTypes.AUTHORIZE_EXECUTE,
      TransactionTypes.CAPTURE,
      TransactionTypes.REFUND,
      TransactionTypes.BANK_TRANSFER,
      TransactionTypes.MANUAL_PAYMENT,
      TransactionTypes.WALLET,
      TransactionTypes.WALLET_REFUND_OUT,
      TransactionTypes.AGREEMENT_EXECUTE,
      TransactionTypes.AGREEMENT_AUTHORIZE,
      TransactionTypes.AGREEMENT_CAPTURE,
      TransactionTypes.AGREEMENT_CANCEL
    ]
  })
  public readonly transactionTypes!: TransactionTypes[];

  @Prop({ default: () => [] })
  public readonly statuses!: TransactionStatus[];

  @Prop({ default: null })
  public invoiceId!: IInvoice["id"];

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data
  public isMultibrand!: boolean;
  public isProcessing = false;
  public dataModule = DataModules.PAYMENTS;
  public filters: IFilter[] = TransactionFilters({
    statuses: this.statuses,
    clientId: this.clientId
  });
  public sorters: ISorter[] = TransactionSorters();

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $transactionsData: this.providerData(),
        $transactionsMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.getData();
  }

  // Computed

  public get transactions() {
    return this.table.data as IPaymentLog[];
  }

  public get resultsIncludeRecentRefunds() {
    return !!this.$_.filter(this.table.data, (log: IPaymentLog) => {
      const logCreatedAt = this.$moment.utc(log.created_at).toDate();
      const sevenDaysAgo = this.$moment()
        .subtract(7, "days")
        .startOf("day")
        .toDate();
      return (
        [TransactionTypes.REFUND].includes(log.transaction_type) &&
        logCreatedAt > sevenDaysAgo
      );
    }).length;
  }

  public get showClient() {
    return !this.invoiceId && !this.clientId;
  }

  public get showInvoice() {
    return !this.invoiceId;
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    // Overrides mixin method
    return this.$_.merge(params, {
      params: {
        with:
          "client,client.image,gateway,brand,invoice,client_payment_details",
        ...this.getFilterStatuses(),
        ...this.getFilterForInvoice(),
        ...this.getFilterForClient(),
        ...this.getFilterForTransactionTypes()
      }
    });
  }

  public getFilterForInvoice() {
    return this.invoiceId ? { "filter[invoice_id]": this.invoiceId } : {};
  }

  public getFilterForTransactionTypes() {
    return {
      "filter[transaction_type]": this.transactionTypes.join(",")
    };
  }

  public getFilterStatuses() {
    return !this.$_.isEmpty(this.statuses)
      ? {
          "filter[status]": this.statuses.map(i => this.$_.toUpper(i)).join(",")
        }
      : {};
  }

  public getFilterForClient() {
    return this.clientId ? { "filter[client_id]": this.clientId } : {};
  }

  public reloadData() {
    return this.getData({ ignoreStored: true });
  }

  // Provide

  @Provide("transactionsProviderMethods")
  public get providerMethods(): ITransactionsProviderMethods {
    return {
      reloadData: this.reloadData,
      getData: this.getData,
      onPageChange: this.onPageChange,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onSort: this.onSort
    };
  }

  @Provide("transactionsProvider")
  public providerData(): ITransactionsProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      clientId: this.clientId,
      constQueryFilters: this.constQueryFilters,
      defaultFilters: this.defaults.filters,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      invoiceId: this.invoiceId,
      isLoading: this.table.loading,
      isMultibrand: this.isMultibrand,
      limit: this.table.limit,
      page: this.table.page,
      requestError: this.requestError,
      resultsIncludeRecentRefunds: this.resultsIncludeRecentRefunds,
      showClient: this.showClient,
      showInvoice: this.showInvoice,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total,
      transactions: this.transactions
    };
  }
}
